import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ArrowRight, Video, Settings2, Loader2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { meetingsService } from "@/services";
import type { MeetingDto } from "@shared/types/MeetingDto";

const TYPE_LABELS: Record<string, string> = {
  INTRO_DISCOVERY: "Discovery",
  STRATEGIC_CONSULTATION: "Strategic",
  TECHNICAL_SCOPING: "Technical",
  SERVICE_ORDER_REFINEMENT: "Refinement",
  MILESTONE_DEMO: "Demo",
  OFFICE_HOURS: "Office Hours",
};

function getMinutesUntil(dateStr: string): number {
  return (new Date(dateStr).getTime() - Date.now()) / 60000;
}

function formatMeetingDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isToday = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  const time = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  if (isToday) return `Today at ${time}`;
  if (isTomorrow) return `Tomorrow at ${time}`;
  return `${date.toLocaleDateString([], { month: "short", day: "numeric" })} at ${time}`;
}

function MeetingRow({ meeting }: { meeting: MeetingDto }) {
  const navigate = useNavigate();
  const minutesUntil = meeting.scheduledAt ? getMinutesUntil(meeting.scheduledAt) : Infinity;
  const isStartingSoon = minutesUntil <= 15 && minutesUntil > -meeting.durationMinutes;

  const handleManage = () => {
    navigate("/meetings", { state: { highlightMeetingId: meeting.id } });
  };

  const handleJoin = () => {
    if (meeting.meetingUrl) {
      window.open(meeting.meetingUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
          <CalendarDays className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{meeting.title}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {meeting.displayName && <span>{meeting.displayName}</span>}
            {meeting.displayName && meeting.scheduledAt && <span>·</span>}
            {meeting.scheduledAt && <span>{formatMeetingDate(meeting.scheduledAt)}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant="info" className="text-[10px]">
          {TYPE_LABELS[meeting.type] ?? meeting.type}
        </Badge>
        {isStartingSoon && meeting.meetingUrl ? (
          <Button size="sm" onClick={handleJoin} className="h-7 text-xs gap-1">
            <Video className="h-3 w-3" /> Join
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={handleManage} className="h-7 text-xs gap-1">
            <Settings2 className="h-3 w-3" /> Manage
          </Button>
        )}
      </div>
    </div>
  );
}

export function MeetingsCard() {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState<MeetingDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUpcoming = useCallback(async () => {
    try {
      const all = await meetingsService.getMeetings();
      const now = Date.now();
      const upcoming = all
        .filter((m) => m.status === "CONFIRMED" && m.scheduledAt && new Date(m.scheduledAt).getTime() > now - 60 * 60000)
        .sort((a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime());
      setMeetings(upcoming);
    } catch {
      // non-critical
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUpcoming();
  }, [fetchUpcoming]);

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-base font-semibold">Upcoming Meetings</CardTitle>
        <Button variant="ghost" size="sm" onClick={() => navigate("/meetings")} className="text-xs">
          View all <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : meetings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <CalendarDays className="h-6 w-6 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground mb-3">No upcoming meetings</p>
            <Button variant="outline" size="sm" onClick={() => navigate("/meetings")}>
              Schedule a meeting
            </Button>
          </div>
        ) : (
          <div className="space-y-1">
            {meetings.slice(0, 5).map((meeting) => (
              <MeetingRow key={meeting.id} meeting={meeting} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
